import { askConfirm } from '../lib/askConfirm';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Mic, Square, Trash2, Loader2 } from 'lucide-react';
import { uid } from '../lib/model';
import { getMode } from '../lib/profiles';
import { aiTranscribe, speechLang } from '../lib/photoAi';
import { fmtDur, useObjectUrl } from '../lib/media';
import { continuousOk, freshPart, speakPunct, joinSpeech } from '../lib/speech';

export function VoiceRecorder({ t, onDone, testid, compact }) {
  const [rec, setRec] = useState(false);
  const [live, setLive] = useState('');
  const [secs, setSecs] = useState(0);
  const [busy, setBusy] = useState(false);
  const ctx = useRef(null);

  const start = async () => {
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch { return toast.error(t.micErr); }
    const mr = new MediaRecorder(stream);
    const chunks = [];
    const c = { mr, stream, chunks, finals: [], interim: '', t0: Date.now() };
    mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    mr.onstop = async () => {
      stream.getTracks().forEach((tr) => tr.stop());
      const dev = [...c.finals, c.interim].reduce(joinSpeech, '').trim();
      const blob = new Blob(chunks, { type: mr.mimeType || 'audio/webm' });
      let transcript = dev, stt = 'device';
      if (getMode() === 'photo' && navigator.onLine && blob.size > 800) {
        setBusy(true);
        try { const ai = await aiTranscribe(blob); if (ai) { transcript = ai; stt = 'ai'; } } catch { /* offline / no licence -> device text */ }
        setBusy(false);
      }
      onDone({ id: uid('memo'), blob, mime: mr.mimeType || 'audio/webm', duration: (Date.now() - c.t0) / 1000, transcript, stt, createdAt: Date.now() });
    };
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      const sr = new SR();
      sr.lang = speechLang(t);
      sr.continuous = continuousOk;
      sr.interimResults = true;
      const mem = { last: '', at: 0 };
      sr.seen = new Set();
      sr.onstart = () => { sr.seen = new Set(); };
      sr.onresult = (e) => {
        let interim = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) {
            if (sr.seen.has(i)) continue;
            sr.seen.add(i);
            const part = freshPart(mem, e.results[i][0].transcript);
            if (part) c.finals.push(speakPunct(part, t.locale));
          }
          else interim += e.results[i][0].transcript;
        }
        c.interim = interim;
        setLive([...c.finals, interim].join(' '));
      };
      sr.onend = () => { if (c.active) try { sr.start(); } catch { /* ignore */ } };
      c.sr = sr;
      c.active = true;
      try { sr.start(); } catch { /* ignore */ }
    } else {
      toast.message(t.noSpeech);
    }
    c.timer = setInterval(() => setSecs((Date.now() - c.t0) / 1000), 250);
    mr.start();
    ctx.current = c;
    setLive('');
    setSecs(0);
    setRec(true);
  };

  const stop = () => {
    const c = ctx.current;
    if (!c) return;
    c.active = false;
    clearInterval(c.timer);
    try { c.sr?.stop(); } catch { /* ignore */ }
    setTimeout(() => c.mr.state !== 'inactive' && c.mr.stop(), 350);
    ctx.current = null;
    setRec(false);
  };

  if (!rec) {
    return (
      <button className={`btn btn-outline ${compact ? 'btn-sm' : ''}`} data-testid={testid} onClick={start} disabled={busy}>
        {busy ? <Loader2 size={15} className="spin" /> : <Mic size={15} />} {busy ? t.fxStt : compact ? '' : t.btnMemo}
      </button>
    );
  }
  return (
    <div className="rec-box" data-testid={`${testid}-recording`}>
      <span className="rec-dot" /> <b>{t.recording} {fmtDur(secs)}</b>
      <span className="rec-live" data-testid={`${testid}-live`}>{live}</span>
      <button className="btn btn-red btn-sm" data-testid={`${testid}-stop`} onClick={stop}><Square size={13} /> {t.btnStop}</button>
    </div>
  );
}

function Memo({ t, m, i, prefix, onDelete }) {
  const url = useObjectUrl(m.blob);
  return (
    <div className="memo" data-testid={`${prefix}-memo-${i}`}>
      <audio controls src={url || undefined} preload="metadata" />
      <span className="memo-dur">{fmtDur(m.duration)}</span>
      {m.transcript && <p className="memo-text">„{m.transcript}“</p>}
      <button className="icon-btn danger" data-testid={`${prefix}-memo-delete-${i}`} onClick={() => askConfirm(t.confirmDelMemo).then((ok) => ok && onDelete(m.id))}><Trash2 size={14} /></button>
    </div>
  );
}

export function MemoList({ t, memos = [], prefix, onDelete }) {
  if (!memos.length) return null;
  return (
    <div className="memo-list">
      {memos.map((m, i) => <Memo key={m.id} t={t} m={m} i={i} prefix={prefix} onDelete={onDelete} />)}
    </div>
  );
}
